import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Brain,
  Activity,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  ArrowLeft,
  LogOut,
  Users,
  Zap,
  Target,
  Award,
  ShieldCheck,
  ThumbsUp,
  ThumbsDown,
  Eye,
  ChevronRight,
  BarChart3,
  Lightbulb,
  GraduationCap
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AgentLearningDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [recentOverrides, setRecentOverrides] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const agents = ['sarah', 'james', 'denise', 'alex', 'emma'];
  
  const agentInfo = {
    sarah: { name: 'SARAH', fullName: 'Screening & Application Review', color: 'purple', icon: Users },
    james: { name: 'JAMES', fullName: 'Joint Arrears Management', color: 'blue', icon: TrendingUp },
    denise: { name: 'DENISE', fullName: 'Dynamic Engineering & Notification', color: 'orange', icon: Zap },
    alex: { name: 'ALEX', fullName: 'Automated Legal & Compliance', color: 'green', icon: ShieldCheck },
    emma: { name: 'EMMA', fullName: 'Expert Management & Mediation', color: 'pink', icon: Award }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, metricsRes, patternsRes, approvalsRes, overridesRes] = await Promise.all([
        api.get('/agent-learning/dashboard'),
        api.get('/agent-learning/metrics'),
        api.get('/agent-learning/patterns'),
        api.get('/agent-learning/governance/pending'),
        api.get('/agent-learning/overrides?limit=10')
      ]);
      
      setDashboard(dashRes.data);
      setMetrics(metricsRes.data);
      setPatterns(patternsRes.data || []);
      setPendingApprovals(approvalsRes.data || []);
      setRecentOverrides(overridesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch learning dashboard:', error);
      toast.error('Failed to load agent learning data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleApproval = async (approvalId, approved) => {
    try {
      await api.post(`/agent-learning/governance/${approvalId}/decide`, {
        approved,
        notes: approved ? 'Approved via dashboard' : 'Rejected via dashboard'
      });
      toast.success(approved ? 'Action approved' : 'Action rejected');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to process approval');
    }
  };

  const initializePatterns = async () => {
    try {
      await api.post('/agent-learning/patterns/initialize-expert');
      toast.success('Expert patterns initialized');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to initialize patterns');
    }
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 0.8) return 'text-green-600';
    if (rate >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAgentMetrics = (agentName) => {
    if (!metrics) return null;
    return metrics[agentName] || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-slate-600">Loading agent learning data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20 sm:pb-6" data-testid="agent-learning-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b px-4 py-3 sm:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                Agent Learning System
              </h1>
              <p className="text-xs sm:text-sm text-slate-500">Self-learning AI staff performance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-600">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-6">
        {/* System Status */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                <Activity className="w-4 h-4" />
                System Status
              </div>
              <div className="text-xl font-bold text-green-600 capitalize">
                {dashboard?.system_status || 'Active'}
              </div>
              <p className="text-xs text-slate-500 mt-1">All agents operational</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                <Lightbulb className="w-4 h-4" />
                Learned Patterns
              </div>
              <div className="text-xl font-bold">{dashboard?.pattern_count || 0}</div>
              <p className="text-xs text-slate-500 mt-1">Active decision patterns</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                <Clock className="w-4 h-4" />
                Pending Approvals
              </div>
              <div className="text-xl font-bold text-orange-600">{pendingApprovals.length}</div>
              <p className="text-xs text-slate-500 mt-1">Awaiting human decision</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                <AlertTriangle className="w-4 h-4" />
                Staff Overrides
              </div>
              <div className="text-xl font-bold">{recentOverrides.length}</div>
              <p className="text-xs text-slate-500 mt-1">Learning corrections</p>
            </CardContent>
          </Card>
        </div>

        {/* Agent Cards */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-600" />
            AI Staff Performance
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {agents.map(agent => {
              const info = agentInfo[agent];
              const agentMetrics = getAgentMetrics(agent);
              const Icon = info.icon;
              const tasksProcessed = agentMetrics?.tasks_processed || 0;
              const successRate = agentMetrics?.success_rate || 0;
              const escalationRate = agentMetrics?.escalation_rate || 0;
              
              return (
                <Card 
                  key={agent}
                  className={`cursor-pointer transition-all hover:shadow-lg ${selectedAgent === agent ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setSelectedAgent(selectedAgent === agent ? null : agent)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-10 h-10 rounded-lg bg-${info.color}-100 flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 text-${info.color}-600`} />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm">{info.name}</h3>
                        <p className="text-xs text-slate-500 line-clamp-1">{info.fullName}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Tasks Today</span>
                        <span className="font-semibold">{tasksProcessed}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Success Rate</span>
                        <span className={`font-semibold ${getSuccessRateColor(successRate)}`}>
                          {(successRate * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Escalations</span>
                        <span className="font-semibold">{(escalationRate * 100).toFixed(0)}%</span>
                      </div>
                    </div>

                    {/* Success bar */}
                    <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r from-green-500 to-green-400`}
                        style={{ width: `${successRate * 100}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Selected Agent Details */}
        {selectedAgent && (
          <Card className="border-2 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-blue-600" />
                  {agentInfo[selectedAgent].name} Detailed Metrics
                </span>
                <Button variant="ghost" size="sm" onClick={() => setSelectedAgent(null)}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{getAgentMetrics(selectedAgent)?.tasks_processed || 0}</p>
                  <p className="text-xs text-slate-600">Tasks Processed</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{getAgentMetrics(selectedAgent)?.tasks_succeeded || 0}</p>
                  <p className="text-xs text-slate-600">Succeeded</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{getAgentMetrics(selectedAgent)?.tasks_failed || 0}</p>
                  <p className="text-xs text-slate-600">Failed</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">{getAgentMetrics(selectedAgent)?.tasks_escalated || 0}</p>
                  <p className="text-xs text-slate-600">Escalated</p>
                </div>
              </div>
              
              {/* Agent Patterns */}
              <div className="mt-4">
                <h4 className="text-sm font-semibold mb-2">Active Patterns</h4>
                <div className="flex flex-wrap gap-2">
                  {patterns.filter(p => p.agent_name === selectedAgent).slice(0, 5).map(pattern => (
                    <Badge key={pattern.pattern_id} variant="outline" className="text-xs">
                      {pattern.pattern_type} ({(pattern.success_rate * 100).toFixed(0)}%)
                    </Badge>
                  ))}
                  {patterns.filter(p => p.agent_name === selectedAgent).length === 0 && (
                    <p className="text-xs text-slate-500">No patterns loaded</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Pending Approvals */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="w-5 h-5 text-orange-600" />
                Governance Queue
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingApprovals.length > 0 ? (
                <div className="space-y-3">
                  {pendingApprovals.slice(0, 5).map(approval => (
                    <div key={approval.id} className="p-3 bg-orange-50 border border-orange-100 rounded-lg">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{approval.action_type}</p>
                          <p className="text-xs text-slate-600 truncate">Agent: {approval.agent_name.toUpperCase()}</p>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{approval.reason_flagged}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 px-2 text-green-600 hover:bg-green-50"
                            onClick={(e) => { e.stopPropagation(); handleApproval(approval.id, true); }}
                          >
                            <ThumbsUp className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 px-2 text-red-600 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); handleApproval(approval.id, false); }}
                          >
                            <ThumbsDown className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm">No pending approvals</p>
                  <p className="text-xs">Agents operating autonomously</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Staff Overrides */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Learning Corrections
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentOverrides.length > 0 ? (
                <div className="space-y-3">
                  {recentOverrides.slice(0, 5).map((override, idx) => (
                    <div key={idx} className="p-3 bg-red-50 border border-red-100 rounded-lg">
                      <div className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{override.agent_name?.toUpperCase()}</p>
                          <p className="text-xs text-slate-600 mt-1">
                            <span className="line-through text-red-600">{override.original_decision}</span>
                            <span className="mx-1">→</span>
                            <span className="text-green-600">{override.corrected_decision}</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1 truncate">{override.reason}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm">No recent corrections</p>
                  <p className="text-xs">Agents making good decisions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Learned Patterns */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Expert Patterns ({patterns.length})
              </CardTitle>
              {patterns.length === 0 && (
                <Button size="sm" onClick={initializePatterns}>
                  Initialize Patterns
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {patterns.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {patterns.slice(0, 9).map(pattern => (
                  <div key={pattern.pattern_id} className="p-3 bg-slate-50 rounded-lg border">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <Badge variant="outline" className="text-xs mb-1">
                          {pattern.agent_name?.toUpperCase()}
                        </Badge>
                        <p className="font-medium text-sm truncate">{pattern.pattern_type}</p>
                        <p className="text-xs text-slate-600 line-clamp-2 mt-1">{pattern.description}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-lg font-bold ${getSuccessRateColor(pattern.success_rate)}`}>
                          {(pattern.success_rate * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-slate-500">{pattern.sample_count} samples</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Target className="w-3 h-3 text-slate-400" />
                      <p className="text-xs text-slate-600 truncate">→ {pattern.recommended_action}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No patterns loaded</p>
                <p className="text-xs">Click "Initialize Patterns" to load expert knowledge</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link to="/admin/autonomy">
            <Card className="p-4 hover:bg-slate-50 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-sm">Autonomy Dashboard</p>
                  <p className="text-xs text-slate-500">Events & Tasks</p>
                </div>
                <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />
              </div>
            </Card>
          </Link>
          <Link to="/admin/autonomy/agents">
            <Card className="p-4 hover:bg-slate-50 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-sm">Agent Config</p>
                  <p className="text-xs text-slate-500">Autonomy levels</p>
                </div>
                <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />
              </div>
            </Card>
          </Link>
          <Link to="/admin/autonomy/events">
            <Card className="p-4 hover:bg-slate-50 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="font-medium text-sm">Event Log</p>
                  <p className="text-xs text-slate-500">System events</p>
                </div>
                <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />
              </div>
            </Card>
          </Link>
          <Link to="/admin">
            <Card className="p-4 hover:bg-slate-50 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-sm">Admin Console</p>
                  <p className="text-xs text-slate-500">Full dashboard</p>
                </div>
                <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />
              </div>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AgentLearningDashboard;
